import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@app/components/ui/dialog";
import { Button } from "@app/components/ui/button";
import { CalendarClock, Database, FileText, ExternalLink, Loader2 } from "lucide-react";
import PatientDocuments from "@app/components/PatientDocuments";
import TemporalDossier from "@app/components/TemporalDossier";
import { SourceDocumentProvider } from "@app/components/SourceDocument";
import API from "@app/services/api";
import dayjs from "dayjs";
import { MageXPatientWithFeedbacks } from "@api/src/types/patient";
import type { PatientEvent } from "@api/src/types/responses";
import { PatientSchema, HeaderLayout } from "@app/types/few-schot-schema";
import { Trans } from "@lingui/macro";
import {
  asClinicalDay,
  useTemporalProjection,
} from "@app/utils/use-temporal-projection";

// Used when the backend doesn't (yet) return a header_layout for this
// speciality. Identity fields are hardcoded in ``PatientHeaderCard`` from
// ``PatientIdentity`` columns and don't need a descriptor. Only the badge
// slot is speciality-specific.
const EMPTY_HEADER_LAYOUT: HeaderLayout = {
  badges: [],
};

interface PatientDetailModalProps {
  open: boolean;
  onClose: () => void;
  /** Path to the patient page, e.g. "/patient/calimed123/456". */
  patientPath: string;
  /**
   * Le jour clinique que la modale décrit (AAAA-MM-JJ), `null` = l'état courant.
   *
   * C'est le `as_of` de la ligne qui ouvre la modale, et il ferme un aller-retour : une ligne
   * datée est CRÉÉE par le curseur de la fiche patient (« Ajouter au jeu de données (au
   * 15/06/2026) »), donc la relire doit remettre le curseur là où il était. Ouvrir à aujourd'hui
   * une ligne de juin 2024 ne montrait pas le mauvais dossier, il montrait le bon à la mauvaise
   * date — et une confirmation faite là s'ancrait sur un fait postérieur, que le serveur refuse
   * ensuite d'appliquer à cette ligne (``governs_at``). Le relecteur relisait, la ligne ne
   * bougeait pas, et rien ne le disait.
   */
  at?: string | null;
}

function parsePatientPath(
  path: string,
): { tenantId: string; patientId: string } | null {
  const match = path.match(/^\/patient\/([^/]+)\/([^/]+)$/);
  if (!match) return null;
  return { tenantId: match[1], patientId: match[2] };
}

export function PatientDetailModal({
  open,
  onClose,
  patientPath,
  at = null,
}: PatientDetailModalProps) {
  const [activeTab, setActiveTab] = useState<"data" | "documents">("data");
  const [loading, setLoading] = useState(false);
  const [patient, setPatient] = useState<MageXPatientWithFeedbacks | null>(
    null,
  );
  const [schema, setSchema] = useState<PatientSchema>({});
  const [events, setEvents] = useState<PatientEvent[]>([]);
  const [headerLayout, setHeaderLayout] = useState<HeaderLayout | null>(null);

  const parsed = parsePatientPath(patientPath);

  // La date de la ligne, filtrée — voir ``at``. Pas de curseur ici : la date n'est pas une commande
  // de la modale, c'est une propriété de la ligne qui l'ouvre. La déplacer voudrait dire modifier
  // la ligne, ce qui est un autre geste et se fait depuis la fiche.
  const clinicalDay = asClinicalDay(at);
  const projection = useTemporalProjection(
    parsed?.tenantId,
    parsed?.patientId,
    clinicalDay,
    open,
  );

  const fetchPatient = useCallback(async () => {
    const p = parsePatientPath(patientPath);
    if (!p) return;
    setLoading(true);
    try {
      const res = await API.get({
        path: `/patient/${p.tenantId}/${p.patientId}`,
      });
      if (res?.ok && res.data) {
        setSchema(res.data.schema || {});
        setHeaderLayout(res.data.header_layout ?? null);
        setEvents(
          (res.data.events || []).sort(
            (a: { debut?: string }, b: { debut?: string }) => {
              if (!a.debut && !b.debut) return 0;
              if (!a.debut) return 1;
              if (!b.debut) return -1;
              return dayjs(b.debut).valueOf() - dayjs(a.debut).valueOf();
            },
          ),
        );
        // One dossier per patient: identity and dossier columns, no clinical value.
        setPatient(res.data.patients?.[0] ?? null);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [patientPath]);

  /** Relire après un geste : la fiche, et la projection datée.
   *
   *  Les deux ensemble. Une validation écrit dans le journal ET déplace la vérité ; ne rafraîchir
   *  que l'une des deux lectures laisserait la moitié de l'écran sur l'état d'avant le geste. */
  const reloadProjection = projection.reload;
  const reloadDossier = useCallback(() => {
    fetchPatient();
    reloadProjection();
  }, [fetchPatient, reloadProjection]);

  useEffect(() => {
    if (open) {
      setPatient(null);
      setSchema({});
      setEvents([]);
      setHeaderLayout(null);
      fetchPatient();
    }
  }, [open, fetchPatient]);

  const effectiveHeaderLayout = headerLayout ?? EMPTY_HEADER_LAYOUT;

  if (!parsed) return null;

  // Identity (name, NISS, birthday, sexe) comes from ``PatientIdentity`` via
  // ``_inject_identity`` on the backend. The DialogTitle derives ``fullName``
  // from the same patient object the header card reads.
  const fullName = patient
    ? `${patient.prenom ?? ""} ${patient.nom ?? ""}`.trim() || "Patient"
    : "Patient";

  // The patient page seeds its cursor from `?at=`.
  const fullRecordPath = clinicalDay
    ? `${patientPath}?at=${clinicalDay}`
    : patientPath;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[92vh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-6 pt-5 pb-0 flex-shrink-0">
          <DialogTitle className="flex flex-wrap items-center gap-3 text-xl font-bold text-slate-900">
            {loading ? (
              <span className="flex items-center gap-2 text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Chargement...
              </span>
            ) : (
              <span>{fullName}</span>
            )}
            {/* Quel JOUR cette modale montre, dit avec la pastille de la colonne « Date clinique »
                de la ligne qui l'ouvre. Le taire referait le défaut qu'on vient de corriger : deux
                écrans qui montrent « le dossier » et qui divergent sans l'annoncer. */}
            {clinicalDay && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
                <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                <Trans>au {dayjs(clinicalDay).format("DD/MM/YYYY")}</Trans>
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Tabs navigation */}
        <div className="border-b border-slate-200 px-6 flex-shrink-0 mt-3">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab("data")}
              className={`px-5 py-2.5 font-medium text-sm border-b-2 transition-colors ${
                activeTab === "data"
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <Database className="h-4 w-4" />
                <Trans>Données structurées</Trans>
              </span>
            </button>
            <button
              onClick={() => setActiveTab("documents")}
              className={`px-5 py-2.5 font-medium text-sm border-b-2 transition-colors ${
                activeTab === "documents"
                  ? "border-primary text-primary"
                  : "border-transparent text-slate-600 hover:text-slate-900 hover:border-slate-300"
              }`}
            >
              <span className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                <Trans>Documents</Trans>
              </span>
            </button>
          </div>
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Une seule attente pour les deux lectures — la fiche et la projection datée — et la
              seconde doit compter : l'en-tête annonce une DATE, donc montrer sous elle les valeurs
              de la ligne précédente, même un instant, serait le défaut que tout ceci corrige. Deux
              lignes du même patient à deux dates, c'est précisément le cas d'usage. */}
          {((loading && !patient) ||
            (activeTab === "data" && !projection.ready)) && (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Chargement des données...
            </div>
          )}

          {/* ── Le dossier temporel ── le même composant que la fiche patient de ce lecteur,
              parcours de soins et barre de clôture compris, projeté au jour de la ligne. */}
          {(!loading || patient) &&
            activeTab === "data" &&
            projection.ready &&
            (patient ? (
              <SourceDocumentProvider
                tenantId={parsed.tenantId}
                patientId={parsed.patientId}
              >
                <TemporalDossier
                  tenantId={parsed.tenantId}
                  patientId={parsed.patientId}
                  patient={patient}
                  schema={schema}
                  headerLayout={effectiveHeaderLayout}
                  projection={projection}
                  events={events}
                  at={clinicalDay}
                  // This modal only opens from a dataset's examples table, so membership is
                  // known without reading it back.
                  inDataset
                  loadPatient={reloadDossier}
                />
              </SourceDocumentProvider>
            ) : (
              <p className="text-slate-500 text-sm text-center py-8">
                <Trans>Aucune donnée structurée disponible.</Trans>
              </p>
            ))}

          {/* ── Documents ── */}
          {(!loading || patient) && activeTab === "documents" && (
            <div className="p-6">
              <PatientDocuments
                tenantId={parsed.tenantId}
                patientId={parsed.patientId}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-200 px-6 py-3 flex items-center justify-between bg-white">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/50"
          >
            {/* La fiche s'ouvre AU MÊME JOUR que la modale : le curseur de la page temporelle
                s'amorce sur `?at=`. Sans ça, « ouvrir la fiche complète » d'une ligne de juin
                2024 atterrissait sur aujourd'hui, et le lecteur voyait la valeur changer en
                passant d'un écran à l'autre sans avoir rien demandé. */}
            <Link to={fullRecordPath} target="_blank">
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
              Ouvrir la fiche complète
            </Link>
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
